import { Modal, notification } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { CodedMedications } from '../../api/fetchDrugSearch';
import AntAutoCompleteV2 from '../../components/prescriptionPad/rightPanel/workspace/padV2/AntAutoComplete';
import { OptionTypeBaseObj } from '../../components/prescriptionPad/rightPanel/workspace/padV2/AntSelect';
import { FORM_ABBR } from '../../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import { GlobalState } from '../../store/reducers';
import { getMedTypeIds } from '../../store/selectors/session';
import { MedicationsEntity } from '../../store/types/prescription';
import { Item } from './ListGenieItems';
import { onMedicineSearch } from './onMedicineSearch';
import { getSelectedClinicId } from '../../store/idbHandler';

type Props = {
    defaultValue: Item;
    closeMe: () => void;
    onSuggestedSelect: (medicine: CodedMedications) => void;
    medTypeIds: string[];
};

type State = {
    selectedMed?: CodedMedications;
    keepName: boolean;
};

class SearchAndReplaceMedicine extends PureComponent<Props, State> {
    state: State = {
        keepName: false,
    };

    renderCellAutosuggestItemJsx = (option: OptionTypeBaseObj): JSX.Element => {
        return (
            <div
                className={`flex items-center justify-between mx-dropdown-item cursor-pointer font-lato w-full`}
            >
                <div className="flex items-center space-x-8">
                    <div className="circle-mx">
                        <p>
                            {FORM_ABBR[
                                ('dosage' in option
                                    ? option.dosage?.dosage_form || ''
                                    : ''
                                ).toLowerCase()
                            ] || 'Mx'}
                        </p>
                    </div>

                    <div>
                        <p className="text-neutral-1 text-14 w-full">{option.name}</p>
                        {option.generic_name && (
                            <p className="text-darwin-neutral-500 text-10 w-full">
                                {option.generic_name}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    onOk = () => {
        const { defaultValue, onSuggestedSelect } = this.props;
        const { selectedMed, keepName } = this.state;
        const currentMed = defaultValue as MedicationsEntity;
        if (!selectedMed) {
            notification.error({
                message: 'Select medicine from suggestions',
                duration: 5,
            });
            return;
        }

        if (keepName) {
            onSuggestedSelect({
                ...selectedMed,
                name: currentMed.name,
                actual_name: selectedMed.name,
            } as CodedMedications);
        } else {
            onSuggestedSelect(selectedMed);
        }
    };

    onSearch = (value: string) => {
        const { defaultValue, medTypeIds = [] } = this.props;
        const currentMed = defaultValue as MedicationsEntity;
        const cat = medTypeIds.join(',');
        return onMedicineSearch(value, cat, currentMed.generic_id, getSelectedClinicId());
    };

    onSelect = (option: Item | undefined) => {
        option && this.setState({ selectedMed: option as CodedMedications });
    };

    render(): ReactNode {
        const { defaultValue, closeMe } = this.props;
        const { selectedMed, keepName } = this.state;
        const { renderCellAutosuggestItemJsx, onOk, onSearch, onSelect } = this;

        const currentMed = defaultValue as MedicationsEntity;
        const form = currentMed.dosage_form || currentMed.form;
        const selectedForm = selectedMed?.dosage?.dosage_form;

        return (
            <Modal
                bodyStyle={{
                    padding: '0px',
                }}
                title={
                    <div className="space-x-4 text-darwin-neutral-900 w-full">
                        <span className="font-700">Fix now</span>
                        <span className="italic">“{currentMed.name}”</span>
                    </div>
                }
                visible
                width={750}
                onCancel={closeMe}
                okText="Confirm"
                onOk={onOk}
            >
                <p className="p-16 text-darwin-neutral-800 bg-darwin-neutral-50">
                    Replace the medicine to ensure 100% patient safety and avoid any medication
                    dispensing error.
                </p>

                <div className="grid grid-cols-12 px-16 py-24">
                    <div className="col-span-5 space-y-10">
                        <p className="text-darwin-neutral-600 font-700 uppercase">
                            Current details
                        </p>

                        <div className="flex items-center justify-between p-10 rounded-8 bg-darwin-neutral-50 border border-darwin-neutral-200">
                            <p>{currentMed.name}</p>

                            {form?.trim() && (
                                <p className="text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-12 bg-white">
                                    {form}
                                </p>
                            )}
                        </div>

                        <p className="text-darwin-neutral-700 p-4">{currentMed.generic_name}</p>

                        <div className="flex items-start space-x-8">
                            <input
                                type="checkbox"
                                id="keep-name"
                                className="w-16 h-16 relative top-4"
                                checked={keepName}
                                onChange={(e) => this.setState({ keepName: e.target.checked })}
                            />
                            <label htmlFor="keep-name" className="select-none">
                                Keep name as <span className="italic">“{currentMed.name}”</span>
                            </label>
                        </div>
                    </div>

                    <div className="col-span-1 flex items-center justify-center">
                        <svg
                            className="w-16 h-14 fill-darwin-accent-bright-blue-800"
                            viewBox="0 0 15 14"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M13.6875 6.3125C14.0938 6.6875 14.0938 7.34375 13.6875 7.71875L8.6875 12.7188C8.40625 13 7.96875 13.0938 7.59375 12.9375C7.21875 12.7812 6.96875 12.4062 6.96875 12V9H1.25C0.53125 9 0 8.46875 0 7.75V6.25C0 5.5625 0.53125 5 1.25 5H7V2C7 1.625 7.21875 1.25 7.59375 1.09375C7.96875 0.9375 8.40625 1.03125 8.6875 1.3125L13.6875 6.3125Z" />
                        </svg>
                    </div>

                    <div className="col-span-6 space-y-12">
                        <p className="text-darwin-neutral-600 font-700 uppercase">Replace with</p>

                        <AntAutoCompleteV2
                            selectedValue={defaultValue}
                            placement={'bottomLeft'}
                            onSearch={onSearch}
                            debounceDelay={300}
                            getLabelToCompare={(o) => (o as MedicationsEntity).name}
                            placeholder={'Start typing Medicines'}
                            onSelect={onSelect}
                            optionsRenderer={renderCellAutosuggestItemJsx}
                            customDropdownClass={`mx-dropdown`}
                            autoFocus
                        />

                        {(selectedMed?.generic_name || selectedForm) && (
                            <div className="flex items-center justify-between space-x-8 p-4">
                                {selectedMed?.generic_name && (
                                    <p className="text-darwin-neutral-700">
                                        {selectedMed.generic_name}
                                    </p>
                                )}

                                {selectedForm && (
                                    <p className="text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-12 bg-white">
                                        {selectedForm}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        );
    }
}

export default connect((state: GlobalState) => ({ medTypeIds: getMedTypeIds(state) }))(
    SearchAndReplaceMedicine,
);
